/**
 * Pure helpers behind `MermaidDiagram` — source sanitising and the
 * token → mermaid `themeVariables` mapping. Kept free of React and the DOM so
 * they unit-test in node; the component supplies a `resolveColor` that reads
 * the live CSS tokens.
 */

/**
 * The only node classes a model may use (`id:::accent`). Each maps to a
 * token-derived `classDef` the renderer injects, so the model marks *meaning*
 * — "this is the step that matters", "this outcome is bad" — and the design
 * system decides the colour.
 */
export const MERMAID_SEMANTIC_CLASSES = ["accent", "success", "warning", "danger", "muted"] as const;

export type MermaidSemanticClass = (typeof MERMAID_SEMANTIC_CLASSES)[number];

const SEMANTIC_CLASS_RE = new RegExp(`^(${MERMAID_SEMANTIC_CLASSES.join("|")})$`);

/**
 * Remove everything in a model-authored source that would override our
 * theme or attach behaviour: a leading YAML frontmatter block (its `config:`
 * can set the theme; its `title:` is redundant with the card's own title),
 * `%%{init}%%` directives, `classDef` / `style` /
 * `linkStyle` lines, `click` handlers, and any `:::class` shorthand outside
 * `MERMAID_SEMANTIC_CLASSES`. The server validator accepts all of these (they
 * parse), so this is the renderer's last word on styling.
 *
 * @param source - Mermaid source as emitted.
 * @returns The source with styling and interaction stripped.
 */
export function sanitizeMermaidSource(source: string): string {
  return source
    .replace(/^\s*---[ \t]*\r?\n[\s\S]*?\r?\n---[ \t]*(\r?\n|$)/, "")
    .replace(/%%\{[\s\S]*?\}%%/g, "")
    .split("\n")
    .filter((line) => !/^\s*(classDef|style|linkStyle|click)\s/.test(line))
    .map((line) => line.replace(/:::([\w-]+)/g, (match, name: string) => (SEMANTIC_CLASS_RE.test(name) ? match : "")))
    .join("\n")
    .trim();
}

/** Diagram-type keywords reported to analytics — a closed enum, never user text. */
export const MERMAID_DIAGRAM_KINDS = [
  "flowchart", "sequence", "state", "class", "er", "journey", "gantt", "timeline",
  "mindmap", "quadrant", "gitgraph", "requirement", "c4", "other",
] as const;

export type MermaidDiagramKind = (typeof MERMAID_DIAGRAM_KINDS)[number];

const KIND_BY_KEYWORD: Record<string, MermaidDiagramKind> = {
  flowchart: "flowchart",
  graph: "flowchart",
  sequencediagram: "sequence",
  statediagram: "state",
  "statediagram-v2": "state",
  classdiagram: "class",
  erdiagram: "er",
  journey: "journey",
  gantt: "gantt",
  timeline: "timeline",
  mindmap: "mindmap",
  quadrantchart: "quadrant",
  gitgraph: "gitgraph",
  requirementdiagram: "requirement",
  c4context: "c4",
  c4container: "c4",
  c4component: "c4",
  c4dynamic: "c4",
  c4deployment: "c4",
};

/**
 * Classify a source by its header keyword, skipping directives and comments.
 *
 * @param source - Mermaid source.
 * @returns The diagram kind, or `other`.
 */
export function mermaidDiagramKind(source: string): MermaidDiagramKind {
  const lines = sanitizeMermaidSource(source).split("\n");
  const header = lines[findHeaderIndex(lines)]?.trim();
  const keyword = header?.split(/[\s:]/)[0]?.toLowerCase() ?? "";
  return KIND_BY_KEYWORD[keyword] ?? "other";
}

/**
 * Index of the diagram-type declaration: the first line that is neither blank
 * nor a `%%` comment. Shared by classification and classDef injection so the
 * two can never disagree about where the header is.
 *
 * @param lines - Sanitised source lines.
 * @returns The header's index, or -1 when there is none.
 */
export function findHeaderIndex(lines: string[]): number {
  return lines.findIndex((line) => {
    const text = line.trim();
    return text !== "" && !text.startsWith("%%");
  });
}

type Rgba = { r: number; g: number; b: number; a: number };

/**
 * Parse a computed CSS colour (`rgb()` / `rgba()`, as `getComputedStyle`
 * reports every colour) into channels.
 *
 * @param value - A computed colour string.
 * @returns The channels, or null when the string is not an rgb colour.
 */
export function parseRgb(value: string): Rgba | null {
  const match = value.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)/i);
  if (!match) return null;
  const alphaRaw = match[4];
  const a = alphaRaw === undefined
    ? 1
    : alphaRaw.endsWith("%") ? parseFloat(alphaRaw) / 100 : parseFloat(alphaRaw);
  return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]), a };
}

/**
 * Parse an opaque `#rrggbb` string.
 *
 * @param hex - Six-digit hex colour.
 * @returns Its channels (alpha 1).
 */
export function hexToRgb(hex: string): Rgba {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
    a: 1,
  };
}

/**
 * Whether a surface colour reads as dark (relative luminance below mid-grey).
 *
 * @param hex - Opaque surface colour.
 * @returns True for dark surfaces.
 */
export function isDarkSurface(hex: string): boolean {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 128;
}

const toHex = ({ r, g, b }: Rgba) =>
  `#${[r, g, b].map((channel) => Math.round(channel).toString(16).padStart(2, "0")).join("")}`;

/**
 * Convert a computed colour to an opaque hex string, compositing any
 * translucency (e.g. `--custom-border` is 10% black) over `backdrop`. Mermaid
 * derives shades from its theme colours, which only works on opaque input.
 *
 * @param value - A computed colour string.
 * @param backdrop - Opaque hex the colour sits on.
 * @returns An opaque `#rrggbb`, or null when `value` is not a colour.
 */
export function toOpaqueHex(value: string, backdrop = "#ffffff"): string | null {
  const color = parseRgb(value);
  if (!color) return null;
  if (color.a >= 1) return toHex(color);
  const base = hexToRgb(backdrop);
  const mix = (top: number, bottom: number) => top * color.a + bottom * (1 - color.a);
  return toHex({ r: mix(color.r, base.r), g: mix(color.g, base.g), b: mix(color.b, base.b), a: 1 });
}

/**
 * CSS expressions the theme is built from, in chat's own token vocabulary.
 * Each carries an inline fallback: an undefined custom property would
 * otherwise resolve to the *inherited* text colour (e.g. a black surface in a
 * host that lacks the token), not to nothing.
 */
export const MERMAID_TOKEN_SOURCES = {
  surface: "var(--custom-card-bg, #ffffff)",
  node: "var(--custom-bg-primary, #f8f8f8)",
  text: "var(--custom-text-primary, #181818)",
  textMuted: "var(--custom-text-secondary, #696969)",
  line: "var(--custom-text-tertiary, #b4b4b4)",
  border: "var(--custom-border, rgba(0, 0, 0, 0.1))",
  accent1: "hsl(var(--v2-chart-1, 24.6 95% 47%))",
  accent2: "hsl(var(--v2-chart-2, 224 72% 38%))",
  accent3: "hsl(var(--v2-chart-3, 202 70% 50%))",
  accent4: "hsl(var(--v2-chart-4, 328 42% 56%))",
  accent5: "hsl(var(--v2-chart-5, 240 6% 44%))",
  // The tenant's brand colour (orange by default, near-black under the
  // monochrome themes) — the one colour that marks "the part that matters".
  brand: "var(--brand-primary, #f97316)",
  success: "hsl(var(--v2-success-text, 161 94% 24%))",
  warning: "hsl(var(--v2-warning-text, 26 90% 37%))",
  danger: "hsl(var(--v2-destructive-text, 0 72% 42%))",
} as const;

export type MermaidTokenName = keyof typeof MERMAID_TOKEN_SOURCES;

/** Used only when the browser cannot compute a token at all. */
export const MERMAID_TOKEN_FALLBACKS: Record<MermaidTokenName, string> = {
  surface: "#ffffff",
  node: "#f8f8f8",
  text: "#181818",
  textMuted: "#696969",
  line: "#b4b4b4",
  border: "#e5e5e5",
  accent1: "#ea580c",
  accent2: "#1b3a9c",
  accent3: "#2692d9",
  accent4: "#b9679a",
  accent5: "#6b6b76",
  brand: "#f97316",
  success: "#047857",
  warning: "#b45309",
  danger: "#b91c1c",
};

/**
 * Mermaid's default journey actor colours. The journey renderer captures its
 * config when the module loads, so neither `initialize({ journey })` nor an
 * `%%{init}%%` directive can override them (verified on 11.17.2).
 */
const JOURNEY_DEFAULT_ACTOR_COLOURS = ["#8FBC8F", "#7CFC00", "#00FFFF", "#20B2AA", "#B0E0E6", "#FFFFE0"];

/**
 * Swap mermaid's hard-wired journey actor colours for our accents in the
 * rendered SVG (legend dots and per-task actor dots share the same fills).
 *
 * @param svg - Rendered journey SVG.
 * @param accents - Replacement colours, applied in actor order.
 * @returns The SVG with themed actor colours.
 */
export function recolorJourneyActors(svg: string, accents: string[]): string {
  return JOURNEY_DEFAULT_ACTOR_COLOURS.reduce((markup, colour, index) => {
    const replacement = accents[index % accents.length];
    return markup.replace(new RegExp(`fill="${colour}"`, "gi"), `fill="${replacement}"`);
  }, svg);
}

/**
 * Give a rendered mermaid SVG explicit pixel dimensions. Mermaid emits
 * `width="100%"` plus a `max-width` style, which has no intrinsic size once
 * the SVG is loaded as an image for PNG export.
 *
 * @param svg - Serialised SVG markup.
 * @param width - Target width in CSS pixels.
 * @param height - Target height in CSS pixels.
 * @returns The markup with fixed `width` / `height` and no inline max-width.
 */
export function withFixedSize(svg: string, width: number, height: number): string {
  return svg.replace(/<svg\b([^>]*)>/, (_match, attributes: string) => {
    const cleaned = attributes
      .replace(/\swidth="[^"]*"/, "")
      .replace(/\sheight="[^"]*"/, "")
      .replace(/max-width:\s*[^;"]+;?/, "");
    return `<svg${cleaned} width="${width}" height="${height}">`;
  });
}

/**
 * Tint `hex` toward `backdrop` — used for mindmap/timeline/journey section
 * fills, which need a light wash of the accent rather than the accent itself
 * so their dark text stays legible.
 *
 * @param hex - Accent colour.
 * @param backdrop - Surface colour to blend toward.
 * @param amount - Share of the accent kept (0–1).
 * @returns The blended opaque hex.
 */
export function tint(hex: string, backdrop: string, amount: number): string {
  const top = hexToRgb(hex);
  const bottom = hexToRgb(backdrop);
  const mix = (channel: "r" | "g" | "b") => top[channel] * amount + bottom[channel] * (1 - amount);
  return toHex({ r: mix("r"), g: mix("g"), b: mix("b"), a: 1 });
}

/**
 * Build mermaid's `base`-theme variables from resolved tokens. Nodes sit on
 * the chat bubble surface with hairline borders and muted connectors, so a
 * diagram reads as part of the answer rather than a pasted image; the chart
 * accents colour only the categorical scales (mindmap branches, timeline
 * periods, journey sections, git branches).
 *
 * @param tokens - Opaque hex per token.
 * @param dark - Whether the app is in dark mode.
 * @param fontFamily - The chat body font stack.
 * @returns A `themeVariables` object for `mermaid.initialize`.
 */
export function buildMermaidThemeVariables(
  tokens: Record<MermaidTokenName, string>,
  dark: boolean,
  fontFamily: string
): Record<string, string | boolean> {
  const accents = [tokens.accent1, tokens.accent2, tokens.accent3, tokens.accent4, tokens.accent5];
  const scale: Record<string, string> = {};
  for (let index = 0; index < 12; index++) {
    const accent = accents[index % accents.length];
    scale[`cScale${index}`] = tint(accent, tokens.surface, dark ? 0.45 : 0.18);
    scale[`cScaleLabel${index}`] = tokens.text;
    scale[`cScalePeer${index}`] = accent;
  }
  const git: Record<string, string> = {};
  for (let index = 0; index < 8; index++) {
    git[`git${index}`] = accents[index % accents.length];
    git[`gitBranchLabel${index}`] = "#ffffff";
  }

  return {
    darkMode: dark,
    fontFamily,
    fontSize: "13px",
    background: tokens.surface,
    primaryColor: tokens.surface,
    primaryTextColor: tokens.text,
    primaryBorderColor: tokens.line,
    secondaryColor: tint(tokens.accent3, tokens.surface, dark ? 0.35 : 0.12),
    secondaryTextColor: tokens.text,
    secondaryBorderColor: tokens.line,
    tertiaryColor: tokens.surface,
    tertiaryTextColor: tokens.text,
    tertiaryBorderColor: tokens.border,
    mainBkg: tokens.surface,
    nodeBorder: tokens.line,
    nodeTextColor: tokens.text,
    textColor: tokens.text,
    titleColor: tokens.text,
    lineColor: tint(tokens.textMuted, tokens.surface, 0.75),
    clusterBkg: tokens.node,
    clusterBorder: tokens.border,
    edgeLabelBackground: tokens.surface,
    // Sequence
    actorBkg: tokens.surface,
    actorBorder: tokens.line,
    actorTextColor: tokens.text,
    actorLineColor: tokens.line,
    signalColor: tokens.textMuted,
    signalTextColor: tokens.text,
    labelBoxBkgColor: tokens.node,
    labelBoxBorderColor: tokens.line,
    labelTextColor: tokens.text,
    loopTextColor: tokens.text,
    noteBkgColor: tint(tokens.brand, tokens.surface, dark ? 0.22 : 0.08),
    noteBorderColor: tint(tokens.brand, tokens.surface, dark ? 0.5 : 0.35),
    noteTextColor: tokens.text,
    activationBkgColor: tokens.node,
    activationBorderColor: tokens.line,
    // State
    labelColor: tokens.text,
    altBackground: tokens.node,
    // Quadrant
    quadrant1Fill: tint(tokens.accent1, tokens.surface, 0.12),
    quadrant2Fill: tint(tokens.accent2, tokens.surface, 0.12),
    quadrant3Fill: tint(tokens.accent5, tokens.surface, 0.12),
    quadrant4Fill: tint(tokens.accent3, tokens.surface, 0.12),
    quadrant1TextFill: tokens.text,
    quadrant2TextFill: tokens.text,
    quadrant3TextFill: tokens.text,
    quadrant4TextFill: tokens.text,
    quadrantPointFill: tokens.accent1,
    quadrantPointTextFill: tokens.text,
    quadrantTitleFill: tokens.text,
    quadrantXAxisTextFill: tokens.textMuted,
    quadrantYAxisTextFill: tokens.textMuted,
    quadrantInternalBorderStrokeFill: tokens.border,
    quadrantExternalBorderStrokeFill: tokens.line,
    ...scale,
    ...git,
  };
}

/**
 * The `classDef` lines behind `MERMAID_SEMANTIC_CLASSES`, appended to
 * flowchart and state sources. Fills are light washes of the status colour so
 * labels keep full-contrast text; the stroke carries the colour.
 *
 * @param tokens - Opaque hex per token.
 * @param dark - Whether the surface is dark.
 * @returns One `classDef` line per semantic class.
 */
export function buildSemanticClassDefs(tokens: Record<MermaidTokenName, string>, dark: boolean): string[] {
  const wash = dark ? 0.24 : 0.1;
  const def = (name: MermaidSemanticClass, colour: string) =>
    `classDef ${name} fill:${tint(colour, tokens.surface, wash)},stroke:${colour},stroke-width:1.5px,color:${tokens.text}`;
  return [
    def("accent", tokens.brand),
    def("success", tokens.success),
    def("warning", tokens.warning),
    def("danger", tokens.danger),
    `classDef muted fill:${tokens.node},stroke:${tokens.border},color:${tokens.textMuted},stroke-dasharray:4 3`,
  ];
}

/**
 * Attach the semantic classes to a sanitised source by declaring their
 * `classDef`s after the header line. Only flowchart and state diagrams
 * support `classDef`; everywhere else the shorthand is dropped so it cannot
 * surface as literal text.
 *
 * @param source - Sanitised mermaid source.
 * @param kind - Its diagram kind.
 * @param tokens - Opaque hex per token.
 * @param dark - Whether the surface is dark.
 * @returns The source ready to render.
 */
export function withSemanticClasses(
  source: string,
  kind: MermaidDiagramKind,
  tokens: Record<MermaidTokenName, string>,
  dark: boolean
): string {
  if (kind !== "flowchart" && kind !== "state") return source.replace(/:::[\w-]+/g, "");
  if (!/:::[\w-]+/.test(source)) return source;
  // Right after the header — never above it, even when comments lead the
  // source. State diagrams only honour a classDef declared before the nodes
  // that use it (flowcharts accept either position).
  const lines = source.split("\n");
  const headerIndex = findHeaderIndex(lines);
  if (headerIndex === -1) return source;
  const defs = buildSemanticClassDefs(tokens, dark).map((line) => `  ${line}`);
  const body = lines.slice(headerIndex + 1);
  // State diagrams ignore a standalone `Won:::success` line (mermaid 11); the
  // equivalent `class Won success` statement is honoured.
  const statements = kind === "state"
    ? body.map((line) => line.replace(/^(\s*)([\w-]+):::([\w-]+)\s*$/, "$1class $2 $3"))
    : body;
  return [...lines.slice(0, headerIndex + 1), ...defs, ...statements].join("\n");
}

/**
 * The finishing layer mermaid's theme variables cannot express — the
 * shadcn-style details that make a diagram read as part of the product:
 * rounded steps with a hairline border and a whisper of shadow, pill edge
 * labels, quiet small-caps group headings, a brand-coloured "main path"
 * (`==>`), softer dotted "optional" edges, and rounded sequence actors and
 * notes. Mermaid scopes it under the diagram's own id.
 *
 * @param tokens - Opaque hex per token.
 * @param dark - Whether the surface is dark.
 * @returns CSS for mermaid's `themeCSS`.
 */
export function buildMermaidThemeCss(tokens: Record<MermaidTokenName, string>, dark: boolean): string {
  const edge = tint(tokens.textMuted, tokens.surface, 0.75);
  const shadow = dark ? "none" : "drop-shadow(0 1px 1px rgba(16, 24, 40, 0.05)) drop-shadow(0 1px 3px rgba(16, 24, 40, 0.06))";
  return `
    .node rect.label-container, .node polygon, .node circle, .node path.label-container { stroke-width: 1px; }
    .node rect.label-container { rx: 8px; ry: 8px; }
    .node { filter: ${shadow}; }
    .node polygon { fill: ${tokens.node}; }
    .nodeLabel, .nodeLabel p { font-weight: 500; line-height: 1.35; }
    .flowchart-link { stroke: ${edge}; stroke-width: 1.5px; }
    .flowchart-link.edge-thickness-thick { stroke: ${tokens.brand}; stroke-width: 2.25px; }
    .flowchart-link.edge-pattern-dotted { stroke: ${tokens.line}; stroke-dasharray: 3 4; }
    marker path { fill: ${edge}; stroke: ${edge}; }
    g.edgeLabel, .labelBkg, span.edgeLabel p { background: transparent !important; }
    span.edgeLabel {
      display: inline-block; padding: 1px 8px; border-radius: 9999px;
      background: ${tokens.surface} !important; border: 1px solid ${tokens.border};
      color: ${tokens.textMuted}; font-size: 11.5px; font-weight: 500;
    }
    span.edgeLabel:empty { display: none; }
    .cluster rect { rx: 12px; ry: 12px; fill: ${tokens.node}; stroke: ${tokens.border}; stroke-width: 1px; }
    .cluster-label .nodeLabel, .cluster-label .nodeLabel p {
      font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: ${tokens.textMuted};
    }
    rect.actor { rx: 8px; ry: 8px; stroke-width: 1px; filter: ${shadow}; }
    text.actor, text.actor > tspan { font-weight: 600; }
    .actor-line { stroke: ${tokens.border}; stroke-dasharray: 3 4; }
    .messageText { font-weight: 500; }
    .messageLine0, .messageLine1 { stroke: ${edge}; stroke-width: 1.5px; }
    #arrowhead path, #crosshead path { fill: ${edge}; stroke: ${edge}; }
    rect.note { rx: 6px; ry: 6px; }
    .labelBox { rx: 4px; ry: 4px; }
    .loopLine { stroke: ${tokens.line}; stroke-dasharray: 3 3; }
    .labelText, .labelText > tspan, .loopText, .loopText > tspan { font-weight: 600; }
    g.stateGroup rect, .node.statediagram-state rect.basic { rx: 8px; ry: 8px; }
    .mindmap-node .nodeLabel, .mindmap-node .nodeLabel p { font-weight: 500; }
    .section-root .nodeLabel, .section-root .nodeLabel p { font-weight: 600; }
  `;
}
